import {
    SITE_TYPE_NEWS_BLOG,
    SITE_TYPE_FOOTBALL_COMPETITION,
    SITE_TYPE_BASIC,
    SITE_TYPE_FOOTBALL_TEAM,
} from "@/config/siteTypes";
import { SiteTypeConfig } from "./types";

/**
 * Common authentication routes shared between all site types.
 * These routes are dynamically enabled based on tenant Firebase configuration.
 */
const authRoutes = [
    { pattern: /^\/sign-in$/, page: "signIn" as const, path: "/sign-in" },
    { pattern: /^\/sign-up$/, page: "signUp" as const, path: "/sign-up" },
    { pattern: /^\/forgot-password$/, page: "forgotPassword" as const, path: "/forgot-password" },
    { pattern: /^\/profile$/, page: "profile" as const, path: "/profile" },
];

/**
 * Default page variants for authentication pages
 */
const authPages = {
    signIn: "sign-in-default" as const,
    signUp: "sign-up-default" as const,
    forgotPassword: "forgot-password-default" as const,
    profile: "profile-default" as const,
};

export const siteTypes: Record<string, SiteTypeConfig> = {
    [SITE_TYPE_NEWS_BLOG]: {
        routes: [
            { pattern: /^\/$/, page: "home", path: "/" },
            { pattern: /^\/football\/competitions$/, page: "footballCompetitions", path: "/football/competitions" },
            {
                pattern: /^\/football\/competitions\/nationsleague$/,
                page: "nationsLeague",
                path: "/football/competitions/nationsleague",
            },
            {
                pattern: /^\/football\/competitions\/nationsleague\/(news|videos|standings|matches|teams)$/,
                page: "nationsLeagueSection",
                params: ["section"],
                path: "/football/competitions/nationsleague/:section",
            },
            {
                pattern: /^\/football\/competitions\/([^/]+)\/standings$/,
                page: "standings",
                params: ["competitionId"],
                path: "/football/competitions/:competitionId/standings",
            },
            {
                pattern: /^\/football\/competitions\/([^/]+)\/matches$/,
                page: "matchesList",
                params: ["competitionId"],
                path: "/football/competitions/:competitionId/matches",
            },
            { pattern: /^\/predictor$/, page: "predictor", path: "/predictor" },
            {
                pattern: /^\/football\/competitions\/([^/]+)\/teams$/,
                page: "teamsList",
                params: ["competitionId"],
                path: "/football/competitions/:competitionId/teams",
            },
            {
                pattern: /^\/football\/competitions\/([^/]+)\/news$/,
                page: "competitionNews",
                params: ["competitionId"],
                path: "/football/competitions/:competitionId/news",
            },
            {
                pattern: /^\/football\/competitions\/([^/]+)\/draw$/,
                page: "competitionDraw",
                params: ["competitionId"],
                path: "/football/competitions/:competitionId/draw",
            },
            {
                pattern: /^\/football\/competitions\/([^/]+)\/videos$/,
                page: "competitionVideos" as const,
                params: ["competitionId"],
                path: "/football/competitions/:competitionId/videos",
            },
            { pattern: /^\/football\/players$/, page: "players", path: "/football/players" },
            {
                pattern: /^\/football\/competitions\/([^/]+)$/,
                page: "footballCompetition",
                params: ["competitionId"],
                path: "/football/competitions/:competitionId",
            },
            {
                pattern: /^\/football\/teams\/([^/]+)$/,
                page: "team",
                params: ["teamId"],
                path: "/football/teams/:teamId",
            },
            {
                pattern: /^\/football\/players\/([^/]+)$/,
                page: "player",
                params: ["playerId"],
                path: "/football/players/:playerId",
            },
            {
                pattern: /^\/football\/match\/([^/]+)$/,
                page: "match",
                params: ["matchId"],
                path: "/football/match/:matchId",
            },
            {
                pattern: /^\/basketball\/match\/([^/]+)$/,
                page: "basketballEvent",
                params: ["matchId"],
                path: "/basketball/match/:matchId",
            },
            {
                pattern: /^\/tennis\/match\/([^/]+)$/,
                page: "tennisEvent",
                params: ["matchId"],
                path: "/tennis/match/:matchId",
            },
            {
                pattern: /^\/hockey\/match\/([^/]+)$/,
                page: "hockeyEvent",
                params: ["matchId"],
                path: "/hockey/match/:matchId",
            },
            {
                pattern: /^\/american-football\/match\/([^/]+)$/,
                page: "americanFootballEvent",
                params: ["matchId"],
                path: "/american-football/match/:matchId",
            },
            { pattern: /^\/articles$/, page: "articles", path: "/articles" },
            { pattern: /^\/articles\/([^/]+)$/, page: "article", params: ["slug"], path: "/articles/:slug" },
            { pattern: /^\/authors$/, page: "authors", path: "/authors" },
            { pattern: /^\/authors\/([^/]+)$/, page: "author", params: ["slug"], path: "/authors/:slug" },
            { pattern: /^\/category\/([^/]+)$/, page: "category", params: ["slug"], path: "/category/:slug" },
            {
                pattern: /^\/category\/([^/]+)\/page\/(\d+)$/,
                page: "category",
                params: ["slug", "page"],
                path: "/category/:slug/page/:page",
            },
            { pattern: /^\/videos$/, page: "videos", path: "/videos" },
            { pattern: /^\/videos\/([^/]+)$/, page: "video", params: ["slug"], path: "/videos/:slug" },
            { pattern: /^\/livescore$/, page: "livescore", path: "/livescore" },
            { pattern: /^\/search$/, page: "search", path: "/search" },
            // Authentication routes
            ...authRoutes,
            // Custom pages - MUST BE LAST (catch-all)
            { pattern: /^\/([a-z0-9-]+)$/, page: "customPage", params: ["slug"], path: "/:slug" },
        ],
        pages: {
            home: "home-news-blog-classic",
            footballCompetitions: "football-competitions-default",
            footballCompetition: "football-competition-classic",
            nationsLeague: "nations-league-hub",
            nationsLeagueSection: "nations-league-section",
            competitionNews: "competition-news-default",
            team: "team-default",
            player: "player-default",
            teamsList: "teams-list-default",
            match: "match-default",
            basketballEvent: "basketball-event-default",
            tennisEvent: "tennis-event-default",
            hockeyEvent: "hockey-event-default",
            americanFootballEvent: "american-football-event-default",
            matchesList: "matches-list-default",
            standings: "standings-default",
            predictor: "predictor-default",
            articles: "articles-default",
            article: "article-default",
            authors: "authors-default",
            author: "author-default",
            category: "category-default",
            videos: "videos-default",
            video: "video-default",
            customPage: "custom-default",
            livescore: "livescore-default",
            competitionDraw: "competition-draw-default",
            competitionVideos: "competition-videos-default",
            players: "players-default",
            search: "search-default",
            ...authPages,
        },
    },

    [SITE_TYPE_FOOTBALL_COMPETITION]: {
        routes: [
            { pattern: /^\/$/, page: "home", path: "/" },
            { pattern: /^\/standings$/, page: "standings", path: "/standings" },
            { pattern: /^\/matches$/, page: "matchesList", path: "/matches" },
            { pattern: /^\/predictor$/, page: "predictor", path: "/predictor" },
            { pattern: /^\/teams$/, page: "teamsList", path: "/teams" },
            { pattern: /^\/teams\/([^/]+)$/, page: "team", params: ["teamId"], path: "/teams/:teamId" },
            { pattern: /^\/players$/, page: "players", path: "/players" },
            { pattern: /^\/players\/([^/]+)$/, page: "player", params: ["playerId"], path: "/players/:playerId" },
            { pattern: /^\/match\/([^/]+)$/, page: "match", params: ["matchId"], path: "/match/:matchId" },
            { pattern: /^\/articles$/, page: "articles", path: "/articles" },
            { pattern: /^\/articles\/([^/]+)$/, page: "article", params: ["slug"], path: "/articles/:slug" },
            { pattern: /^\/authors$/, page: "authors", path: "/authors" },
            { pattern: /^\/authors\/([^/]+)$/, page: "author", params: ["slug"], path: "/authors/:slug" },
            { pattern: /^\/category\/([^/]+)$/, page: "category", params: ["slug"], path: "/category/:slug" },
            {
                pattern: /^\/category\/([^/]+)\/page\/(\d+)$/,
                page: "category",
                params: ["slug", "page"],
                path: "/category/:slug/page/:page",
            },
            { pattern: /^\/videos$/, page: "videos", path: "/videos" },
            { pattern: /^\/videos\/([^/]+)$/, page: "video", params: ["slug"], path: "/videos/:slug" },
            { pattern: /^\/livescore$/, page: "livescore", path: "/livescore" },
            { pattern: /^\/draw$/, page: "competitionDraw", path: "/draw" },
            { pattern: /^\/search$/, page: "search", path: "/search" },
            // Authentication routes
            ...authRoutes,
            // Custom pages - MUST BE LAST (catch-all)
            { pattern: /^\/([a-z0-9-]+)$/, page: "customPage", params: ["slug"], path: "/:slug" },
        ],
        pages: {
            home: "home-football-competition-classic",
            team: "team-default",
            player: "player-default",
            teamsList: "teams-list-default",
            match: "match-default",
            matchesList: "matches-list-default",
            standings: "standings-default",
            predictor: "predictor-default",
            articles: "articles-default",
            article: "article-default",
            authors: "authors-default",
            author: "author-default",
            category: "category-default",
            videos: "videos-default",
            video: "video-default",
            customPage: "custom-default",
            livescore: "livescore-default",
            competitionDraw: "competition-draw-default",
            players: "players-default",
            search: "search-default",
            ...authPages,
        },
    },

    [SITE_TYPE_BASIC]: {
        routes: [
            { pattern: /^\/$/, page: "home", path: "/" },
            { pattern: /^\/articles$/, page: "articles", path: "/articles" },
            { pattern: /^\/articles\/([^/]+)$/, page: "article", params: ["slug"], path: "/articles/:slug" },
            { pattern: /^\/authors$/, page: "authors", path: "/authors" },
            { pattern: /^\/authors\/([^/]+)$/, page: "author", params: ["slug"], path: "/authors/:slug" },
            { pattern: /^\/category\/([^/]+)$/, page: "category", params: ["slug"], path: "/category/:slug" },
            {
                pattern: /^\/category\/([^/]+)\/page\/(\d+)$/,
                page: "category",
                params: ["slug", "page"],
                path: "/category/:slug/page/:page",
            },
            { pattern: /^\/videos$/, page: "videos", path: "/videos" },
            { pattern: /^\/videos\/([^/]+)$/, page: "video", params: ["slug"], path: "/videos/:slug" },
            { pattern: /^\/predictor$/, page: "predictor", path: "/predictor" },
            { pattern: /^\/search$/, page: "search", path: "/search" },
            // Authentication routes
            ...authRoutes,
            // Custom pages — MUST BE LAST (catch-all)
            { pattern: /^\/([a-z0-9-]+)$/, page: "customPage", params: ["slug"], path: "/:slug" },
        ],
        pages: {
            home: "home-basic-default",
            articles: "articles-default",
            article: "article-default",
            authors: "authors-default",
            author: "author-default",
            category: "category-default",
            videos: "videos-default",
            video: "video-default",
            predictor: "predictor-default",
            search: "search-default",
            customPage: "custom-default",
            ...authPages,
        },
    },

    [SITE_TYPE_FOOTBALL_TEAM]: {
        routes: [
            { pattern: /^\/$/, page: "home", path: "/" },
            { pattern: /^\/articles$/, page: "articles", path: "/articles" },
            { pattern: /^\/articles\/([^/]+)$/, page: "article", params: ["slug"], path: "/articles/:slug" },
            { pattern: /^\/authors$/, page: "authors", path: "/authors" },
            { pattern: /^\/authors\/([^/]+)$/, page: "author", params: ["slug"], path: "/authors/:slug" },
            { pattern: /^\/category\/([^/]+)$/, page: "category", params: ["slug"], path: "/category/:slug" },
            {
                pattern: /^\/category\/([^/]+)\/page\/(\d+)$/,
                page: "category",
                params: ["slug", "page"],
                path: "/category/:slug/page/:page",
            },
            { pattern: /^\/videos$/, page: "videos", path: "/videos" },
            { pattern: /^\/videos\/([^/]+)$/, page: "video", params: ["slug"], path: "/videos/:slug" },
            { pattern: /^\/squad$/, page: "squad", path: "/squad" },
            { pattern: /^\/players\/([^/]+)$/, page: "player", params: ["playerId"], path: "/players/:playerId" },
            { pattern: /^\/matches$/, page: "matchesList", path: "/matches" },
            { pattern: /^\/matches\/([^/]+)$/, page: "match", params: ["matchId"], path: "/matches/:matchId" },
            { pattern: /^\/teams\/([^/]+)$/, page: "team", params: ["teamId"], path: "/teams/:teamId" },
            { pattern: /^\/standings$/, page: "standings", path: "/standings" },
            { pattern: /^\/search$/, page: "search", path: "/search" },
            // Authentication routes
            ...authRoutes,
            // Custom pages — MUST BE LAST (catch-all)
            { pattern: /^\/([a-z0-9-]+)$/, page: "customPage", params: ["slug"], path: "/:slug" },
        ],
        pages: {
            home: "home-football-team-editorial",
            articles: "articles-default",
            article: "article-default",
            authors: "authors-default",
            author: "author-default",
            category: "category-default",
            videos: "videos-default",
            video: "video-default",
            squad: "squad-default",
            player: "player-default",
            matchesList: "matches-list-team",
            match: "match-default",
            team: "team-default",
            standings: "standings-default",
            search: "search-default",
            customPage: "custom-default",
            ...authPages,
        },
    },
};
